import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  RefreshCw, 
  Search, 
  Merge, 
  Package, 
  Users,
  FileText
} from 'lucide-react';
import { useMergeDeals, ContactWithDuplicateDeals } from '@/hooks/useMergeDeals';
import { MergeDealsDialog } from '@/components/deals/MergeDealsDialog';
import { formatCurrency } from '@/utils';
import { useAuth } from '@/hooks/useAuth';

export default function MergeDeals() {
  const { user } = useAuth();
  const { 
    duplicates, 
    loading, 
    merging,
    fetchDuplicates, 
    mergeMultipleDeals 
  } = useMergeDeals();

  const [searchTerm, setSearchTerm] = useState('');
  const [onlyQuickBooks, setOnlyQuickBooks] = useState(false);
  const [selectedContact, setSelectedContact] = useState<ContactWithDuplicateDeals | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchDuplicates(onlyQuickBooks);
  }, [fetchDuplicates, onlyQuickBooks]);

  const handleRefresh = () => {
    fetchDuplicates(onlyQuickBooks);
  };

  const handleMerge = async (dealDestinoId: string, dealOrigemIds: string[]) => {
    const success = await mergeMultipleDeals(
      dealDestinoId, 
      dealOrigemIds,
      user?.id
    );
    if (success) {
      fetchDuplicates(onlyQuickBooks);
    }
  };

  const openMergeDialog = (contact: ContactWithDuplicateDeals) => {
    setSelectedContact(contact);
    setDialogOpen(true);
  };

  // Filter duplicates by search term
  const filteredDuplicates = duplicates.filter(contact => {
    if (!searchTerm) return true;
    const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.toLowerCase();
    const email = (contact.primary_email || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    return fullName.includes(search) || email.includes(search);
  });

  // Stats
  const totalWithQB = duplicates.filter(c => c.deals_qb > 0).length;

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Merge className="h-6 w-6" />
            Mesclar Deals Duplicados
          </h1>
          <p className="text-muted-foreground mt-1">
            Unifique deals do mesmo cliente, incluindo histórico importado do QuickBooks
          </p>
        </div>
        <Button onClick={handleRefresh} disabled={loading} className="gap-2">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{duplicates.length}</p>
                <p className="text-sm text-muted-foreground">Clientes com duplicados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-secondary/50 rounded-lg">
                <FileText className="h-5 w-5 text-secondary-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalWithQB}</p>
                <p className="text-sm text-muted-foreground">Com Histórico QuickBooks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-muted rounded-lg">
                <Package className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {duplicates.reduce((sum, c) => sum + c.total_deals, 0)}
                </p>
                <p className="text-sm text-muted-foreground">Total de deals</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="only-qb"
                checked={onlyQuickBooks}
                onCheckedChange={(checked) => setOnlyQuickBooks(checked === true)}
              />
              <Label htmlFor="only-qb" className="cursor-pointer">
                Só com Histórico QuickBooks
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardHeader>
          <CardTitle>Clientes com Deals Duplicados</CardTitle>
          <CardDescription>
            {filteredDuplicates.length} cliente(s) encontrado(s)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-4 p-4 border rounded-lg">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                  <Skeleton className="h-10 w-24" />
                </div>
              ))}
            </div>
          ) : filteredDuplicates.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Merge className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum cliente com deals duplicados encontrado</p>
              {onlyQuickBooks && (
                <p className="text-sm mt-2">
                  Tente desmarcar o filtro "Só com Histórico QuickBooks"
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredDuplicates.map((contact) => (
                <div
                  key={contact.contact_id}
                  className="flex flex-col lg:flex-row lg:items-center gap-4 p-4 border rounded-lg hover:bg-muted/30 transition-colors"
                >
                  {/* Contact Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold truncate">
                        {contact.first_name} {contact.last_name}
                      </h3>
                      <Badge variant="outline">
                        {contact.total_deals} deals
                      </Badge>
                      {contact.deals_qb > 0 && (
                        <Badge variant="secondary">
                          {contact.deals_qb} QB
                        </Badge>
                      )}
                    </div>
                    {contact.primary_email && (
                      <p className="text-sm text-muted-foreground truncate">
                        {contact.primary_email}
                      </p>
                    )}
                  </div>

                  {/* Deals Preview */}
                  <div className="flex-1 space-y-1">
                    {contact.deals.slice(0, 3).map((deal, idx) => (
                      <div key={deal.id} className="flex items-center gap-2 text-sm">
                        <span className="text-muted-foreground">•</span>
                        <span className="truncate max-w-[200px]">{deal.name}</span>
                        <Badge variant="outline" className="shrink-0">
                          {formatCurrency(deal.total_amount)}
                        </Badge>
                        {deal.payments_count > 0 && (
                          <span className="text-xs text-muted-foreground shrink-0">
                            ({deal.payments_count} pmts)
                          </span>
                        )}
                        {deal.source === 'quickbooks' && (
                          <Badge variant="secondary" className="text-xs shrink-0">
                            QB
                          </Badge>
                        )}
                      </div>
                    ))}
                    {contact.deals.length > 3 && (
                      <p className="text-xs text-muted-foreground pl-4">
                        +{contact.deals.length - 3} mais...
                      </p>
                    )}
                  </div>

                  {/* Action */}
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => openMergeDialog(contact)}
                    className="shrink-0 gap-2"
                  >
                    <Merge className="h-4 w-4" />
                    Mesclar
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Merge Dialog */}
      {selectedContact && (
        <MergeDealsDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          contactName={`${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`}
          deals={selectedContact.deals}
          onConfirm={handleMerge}
          merging={merging}
        />
      )}
    </div>
  );
}
